import { useState, useEffect } from "react";
import { Loader2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const HOURS = Array.from({ length: 14 }, (_, i) => i + 8); // 08:00 - 21:00

// Editable grid for teacher; read-only display for family (no click actions)
export default function AvailabilityGrid({ teacherId, editable = false }) {
  const [slots, setSlots] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/teacher/availability/${teacherId}`);
        setSlots(new Set((data.slots || []).map((s) => `${s.day}-${s.hour}`)));
      } finally {
        setLoading(false);
      }
    })();
  }, [teacherId]);

  const key = (d, h) => `${d}-${h}`;

  const handleClick = (d, h) => {
    if (!editable) return;
    const k = key(d, h);
    const next = new Set(slots);
    next.has(k) ? next.delete(k) : next.add(k);
    setSlots(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const arr = Array.from(slots).map((k) => {
        const [d, h] = k.split("-").map(Number);
        return { day: d, hour: h };
      });
      await api.put("/teacher/availability", { slots: arr });
      toast.success("Müsaitlik kaydedildi");
    } catch {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-neutral-500">Yükleniyor...</div>;

  return (
    <div>
      <div className="overflow-x-auto">
        <div className="min-w-[560px]">
          <div className="grid grid-cols-8 gap-1 mb-1">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 py-1"></div>
            {DAYS.map((d) => (
              <div key={d} className="text-[11px] font-semibold text-center text-slate-700 py-1">{d}</div>
            ))}
          </div>
          {HOURS.map((h) => (
            <div key={h} className="grid grid-cols-8 gap-1 mb-1">
              <div className="text-[11px] font-medium text-slate-500 text-right pr-2 py-2">
                {String(h).padStart(2, "0")}:00
              </div>
              {DAYS.map((_, d) => {
                const active = slots.has(key(d, h));
                const common = `h-9 rounded-md text-[11px] font-medium transition-all flex items-center justify-center ${
                  active
                    ? editable
                      ? "bg-[color:var(--brand)] text-white hover:bg-[color:var(--brand-hover)]"
                      : "bg-[color:var(--mint)] text-white"
                    : editable
                    ? "bg-slate-100 hover:bg-slate-200 border border-slate-200"
                    : "bg-slate-50 border border-dashed border-slate-200 opacity-50"
                }`;
                if (!editable) {
                  return (
                    <div key={d} data-testid={`slot-${d}-${h}`} className={`${common} cursor-default select-none`}>
                      {active ? "✓" : ""}
                    </div>
                  );
                }
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => handleClick(d, h)}
                    data-testid={`slot-${d}-${h}`}
                    className={common}
                  >
                    {active ? "✓" : ""}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {editable && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-xs text-slate-500">Müsait olduğun 1 saatlik dilimlere tıkla. Aileler bu takvim üzerinden müsait saatlerini yalnızca görüntüler.</p>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-availability-btn"
            className="inline-flex items-center gap-2 bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white rounded-full px-5 h-10 text-sm font-medium active:scale-95 transition-transform"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Kaydet
          </button>
        </div>
      )}
    </div>
  );
}
